// /plan/terrain — Module 4 DEM library. Lists terrain_assets, uploads
// local GeoTIFFs, and fires /api/terrain/fetch-public for bbox fetches
// (falls back to the nearest bundled preset DEM when no
// OPENTOPOGRAPHY_API_KEY is configured on the server — the caller sees
// a fallback_preset or fallback_unavailable envelope).
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, UploadCloud, Download, MapPin, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import EntityMetadataDialog from "@/components/EntityMetadataDialog";
import { terrain as terrainApi } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));

const SCOPES = [
    { id: "all", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

function StatusChip({ status }) {
    const cls =
        status === "ready"
            ? "bg-ux-success/10 text-ux-success border-ux-success/30"
            : status === "failed"
            ? "bg-ux-danger/10 text-ux-danger border-ux-danger/30"
            : "bg-ux-bg-2 text-ux-text-3 border-ux-border-1";
    return (
        <span
            className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cls}`}
            data-testid="terrain-status-chip"
        >
            {status || "unknown"}
        </span>
    );
}

export default function TerrainPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [scope, setScope] = useState("all");
    const [uploading, setUploading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [name, setName] = useState("my_tile");
    const [bbox, setBbox] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [busyRowId, setBusyRowId] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const fileInputRef = useRef(null);
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const list = await terrainApi.list({ scope });
            setItems(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load terrain"));
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        load();
    }, [load]);

    const selected = useMemo(
        () => items.find((x) => x.id === selectedId) || null,
        [items, selectedId],
    );

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("name", name || f.name.replace(/\.[^.]+$/, ""));
            const created = await terrainApi.upload(fd);
            toast.success(`Uploaded DEM "${created.name}"`);
            await load();
            setSelectedId(created.id);
        } catch (ex) {
            toast.error(formatApiError(ex, "Upload failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onFetchPublic = async () => {
        if (!bbox) {
            toast.error("Shift+drag a bbox on the map first.");
            return;
        }
        setFetching(true);
        try {
            const res = await terrainApi.fetchPublic({
                name: name || "bbox_tile",
                bbox,
            });
            if (res.status === "fallback_preset") {
                toast.success(
                    `No API key — served bundled preset "${res.terrain.name}"`,
                );
                await load();
                setSelectedId(res.terrain.id);
            } else if (res.status === "fallback_unavailable") {
                toast.error("No bundled preset overlaps bbox. Upload a GeoTIFF instead.");
            } else if (res.status === "chunked") {
                toast.warning(
                    `bbox too large — ${res.chunks.length} chunks suggested. ` +
                        `Narrow your selection or request per-chunk.`,
                );
            } else if (res.id) {
                toast.success(`Fetched "${res.name}" from OpenTopography`);
                await load();
                setSelectedId(res.id);
            } else {
                toast.message("Terrain fetch returned an unusual envelope", {
                    description: JSON.stringify(res).slice(0, 120),
                });
            }
        } catch (ex) {
            toast.error(formatApiError(ex, "Fetch failed"));
        } finally {
            setFetching(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this terrain tile?")) return;
        try {
            await terrainApi.remove(id);
            toast.success("Terrain deleted");
            if (selectedId === id) setSelectedId(null);
            await load();
        } catch (ex) {
            toast.error(formatApiError(ex, "Delete failed"));
        }
    };

    // Phase 6 — atomic clone (DEM bytes shared; metadata wrapper forked).
    const onClone = async (id) => {
        setBusyRowId(id);
        try {
            const cloned = await terrainApi.clone(id);
            toast.success(`Cloned as "${cloned.name}"`);
            await load();
            setSelectedId(cloned.id);
        } catch (ex) {
            toast.error(formatApiError(ex, "Clone failed"));
        } finally {
            setBusyRowId(null);
        }
    };

    // Phase 6 — metadata-only PATCH. Server returns 409 with the
    // canonical "Preset is immutable; clone first." for preset rows.
    // Phase 7a — when that 409 fires, we offer an inline `Clone now`
    // action on the toast instead of a dead-end error.
    const onSaveMetadata = async (patch) => {
        if (!editTarget) return;
        try {
            await terrainApi.patch(editTarget.id, patch);
            toast.success("Metadata updated");
            await load();
        } catch (ex) {
            presetImmutabilityToast(ex, {
                entityType: "terrain",
                id: editTarget.id,
                onCloned: async (cloned) => {
                    await load();
                    setSelectedId(cloned.id);
                },
            });
            throw ex;  // bubble so the dialog stays open
        }
    };

    return (
        <div className="space-y-6" data-testid="terrain-page">
            <PageHeader
                title="Terrain"
                subtitle="DEM tiles — upload GeoTIFFs or fetch public SRTM via OpenTopography."
                actions={
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger
                            className="w-[150px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="terrain-scope"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            {SCOPES.map((s) => (
                                <SelectItem
                                    key={s.id}
                                    value={s.id}
                                    className="text-[12px] font-mono"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left: list of tiles */}
                <div
                    className="lg:col-span-2 border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-2"
                    data-testid="terrain-list"
                >
                    <div className="flex items-center justify-between">
                        <h3 className="font-mono text-xs uppercase tracking-wider text-ux-text-3">
                            {items.length} tile{items.length === 1 ? "" : "s"}
                        </h3>
                        <Link
                            to="/plan/obstacles"
                            className="text-xs text-ux-accent underline decoration-dotted"
                        >
                            Obstacles →
                        </Link>
                    </div>
                    {loading ? (
                        <div className="text-xs text-ux-text-3">Loading…</div>
                    ) : err ? (
                        <div
                            className="text-xs text-ux-danger"
                            data-testid="terrain-list-error"
                        >
                            {err}
                        </div>
                    ) : items.length === 0 ? (
                        <EmptyState
                            title="No terrain yet"
                            description="Upload a GeoTIFF or draw a bbox on the map."
                        />
                    ) : (
                        <ul className="space-y-1">
                            {items.map((it) => {
                                const active = it.id === selectedId;
                                return (
                                    <li
                                        key={it.id}
                                        className={`group flex items-start gap-2 px-2 py-2 rounded-md border ${
                                            active
                                                ? "border-ux-accent/60 bg-ux-accent/5"
                                                : "border-ux-border-1 hover:border-ux-border-2"
                                        }`}
                                        data-testid={`terrain-row-${it.id}`}
                                    >
                                        <button
                                            type="button"
                                            className="flex-1 text-left"
                                            onClick={() => setSelectedId(it.id)}
                                            data-testid={`terrain-select-${it.id}`}
                                        >
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium">{it.name}</span>
                                                <StatusChip status={it.status} />
                                                {it.is_preset ? (
                                                    <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                        preset
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="text-[11px] font-mono text-ux-text-3">
                                                {it.dataset || "custom"} · {it.width_px}×
                                                {it.height_px} px
                                            </div>
                                            <div className="text-[10px] font-mono text-ux-text-3">
                                                bbox [{it.bbox?.lon_min?.toFixed(3)},{" "}
                                                {it.bbox?.lat_min?.toFixed(3)}] → [
                                                {it.bbox?.lon_max?.toFixed(3)},{" "}
                                                {it.bbox?.lat_max?.toFixed(3)}]
                                            </div>
                                        </button>
                                        {!it.is_preset ? (
                                            <button
                                                type="button"
                                                onClick={() => setEditTarget(it)}
                                                className="text-ux-text-3 hover:text-ux-accent"
                                                data-testid={`terrain-edit-${it.id}`}
                                                aria-label="Edit metadata"
                                                title="Edit metadata"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={() => onClone(it.id)}
                                            className="text-ux-text-3 hover:text-ux-accent disabled:opacity-50"
                                            data-testid={`terrain-clone-${it.id}`}
                                            aria-label="Clone to my tenant"
                                            title="Clone to my tenant"
                                            disabled={busyRowId === it.id}
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </button>
                                        <PresetPublishButton
                                            entityType="terrain"
                                            entityId={it.id}
                                            entity={it}
                                            user={user}
                                            size="sm"
                                            variant="ghost"
                                            label=""
                                            testId={`terrain-publish-${it.id}`}
                                            onPublished={load}
                                        />
                                        {!it.is_preset ? (
                                            <button
                                                type="button"
                                                onClick={() => onDelete(it.id)}
                                                className="text-ux-text-3 hover:text-ux-danger"
                                                data-testid={`terrain-delete-${it.id}`}
                                                aria-label="Delete"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Right: map + action row */}
                <div className="lg:col-span-3 space-y-3">
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap items-end gap-3"
                        data-testid="terrain-actions"
                    >
                        <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                            Tile name
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-48 h-8 text-[12px] font-mono"
                                data-testid="terrain-name-input"
                            />
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".tif,.tiff,image/tiff"
                            onChange={onUpload}
                            className="hidden"
                            data-testid="terrain-file-input"
                        />
                        <SpinnerButton
                            size="sm"
                            variant="secondary"
                            loading={uploading}
                            onClick={() => fileInputRef.current?.click()}
                            testId="terrain-upload-btn"
                        >
                            <UploadCloud className="h-3.5 w-3.5 mr-1" />
                            Upload GeoTIFF
                        </SpinnerButton>
                        <SpinnerButton
                            size="sm"
                            loading={fetching}
                            onClick={onFetchPublic}
                            disabled={!bbox}
                            testId="terrain-fetch-btn"
                        >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            Fetch public for bbox
                        </SpinnerButton>
                        {bbox ? (
                            <div className="text-[11px] font-mono text-ux-text-3">
                                <MapPin className="inline h-3 w-3 mr-1" />
                                bbox [{bbox.lon_min.toFixed(3)}, {bbox.lat_min.toFixed(3)}] →
                                [{bbox.lon_max.toFixed(3)}, {bbox.lat_max.toFixed(3)}]
                            </div>
                        ) : (
                            <div className="text-[11px] font-mono text-ux-text-3">
                                Shift+drag on map to draw a bbox
                            </div>
                        )}
                    </div>

                    <Suspense
                        fallback={
                            <div
                                data-testid="terrain-map-loading"
                                className="h-[420px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
                            >
                                Loading map…
                            </div>
                        }
                    >
                        <MapEditor
                            centre={[
                                (selected?.bbox?.lat_min + selected?.bbox?.lat_max) / 2 || 51.51,
                                (selected?.bbox?.lon_min + selected?.bbox?.lon_max) / 2 || 0.0,
                            ]}
                            zoom={selected ? 11 : 4}
                            bbox={bbox}
                            onBboxChange={setBbox}
                            terrainTile={selected}
                            obstacles={[]}
                            heightPx={420}
                            testId="terrain-map"
                        />
                    </Suspense>
                </div>
            </div>

            <EntityMetadataDialog
                open={!!editTarget}
                onOpenChange={(o) => !o && setEditTarget(null)}
                entity={editTarget}
                entityType="terrain"
                onSave={onSaveMetadata}
                testId="terrain-metadata-dialog"
            />
        </div>
    );
}
